import { Description, Field, Input, Label } from "@headlessui/react";
import { Player } from "@lobby/core/src/entities";
import { APIError, Button, useErrorTranslate, useMessage } from "@lobby/core/src/shared";
import { useTranslate } from "@lobby/ocb-intl";
import { useState } from "react";
import type { FormEvent } from "react";

export function PasswordForgotForm() {
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState("");

  const message = useMessage();
  const { $t } = useTranslate();
  const { formatMessage: translateError } = useErrorTranslate();

  const { mutate: resetPasswordMutate, isPending } = Player.useResetPassword();

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    setValidationError("");

    const form = new FormData(ev.currentTarget);

    if (validateForm(form)) {
      resetPassword(form);
    }
  }

  function resetPassword(form: FormData) {
    resetPasswordMutate(
      {
        email: form.get("email") as string,
      },
      {
        onSuccess: (data) => {
          const error = data.error;
          if (error) {
            const message = translateError(new APIError(error.message, { code: error.code }));
            setValidationError(message);
          }

          if (data.result) {
            showSuccessMessage();
            setEmail("");
          }
        },
      },
    );
  }

  function validateForm(form: FormData) {
    return form.has("email");
  }

  function showSuccessMessage() {
    message.success(
      $t({ defaultMessage: "We've sent a link to reset your password. Check your inbox." }),
      { duration: 3000 },
    );
  }

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field className="mb-7">
          <Description className="text-12 lg:text-14">
            {$t({
              defaultMessage:
                "Don’t worry. It happens. Just type your email address, and we will send you the instructions on how to reset your password and continue playing.",
            })}
          </Description>
        </Field>
        <Field className="flex flex-col">
          <Label>{$t({ defaultMessage: "E-mail" })}:</Label>
          <Input
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoCapitalize="none"
            minLength={6}
            maxLength={255}
            autoFocus
            required
          />
        </Field>

        {validationError && (
          <div className="text-center text-12 text-carnation lg:text-14">{validationError}</div>
        )}

        <Button variant="secondary" type="submit" loading={isPending} className="mx-auto">
          {$t({ defaultMessage: "Send" })}
        </Button>
      </form>
    </div>
  );
}
